<template>
	<Skeleton :paragraph="{ rows: 14 }" :title="false" :loading="loading">
		<VerticalLayout>
			<TypographyText>{{ $t('settings.vrsSettingsForm.activeSignalsMessages.rules') }}</TypographyText>
			<TypographyText>{{ $t('settings.vrsSettingsForm.activeSignalsMessages.recommendations') }}</TypographyText>
			<div class="signals-settings">
				<div v-for="item in signalsSettings" :key="item.gnssCode">
					<DividerAutoDirection>
						<Tooltip v-if="item.gnssCode === 'C'" placement="topLeft" color="black" :title="$t('settings.vrsSettingsForm.activeSignalsMessages.gnssTooltipC')">
							<QuestionCircleOutlined />
							{{ $t('common.gnssLabels.' + item.gnssCode) }}
						</Tooltip>
						<span v-else>{{ $t('common.gnssLabels.' + item.gnssCode) }}</span>
					</DividerAutoDirection>
					<VerticalLayout size="small">
						<BandActiveSignals
							v-for="(band, bandIndex) in item.bands"
							:key="item.gnssCode + '-' + band.key"
							:subNetworkDeleted="settings.deleted"
							:gnssCode="item.gnssCode"
							:band="band"
							:vrsSignalsStats="vrsSignalsStats"
							@update:band="(updatedBand) => updateBand(item.gnssCode, bandIndex, updatedBand)"
						/>
					</VerticalLayout>
				</div>
			</div>
			<SettingsInfoBanner v-if="formsChanged" status="warning">
				{{ $t('settings.vrsSettingsForm.activeSignalsMessages.restartRequiredHint') }}
			</SettingsInfoBanner>
			<Space>
				<Button type="primary" @click="onSubmit" :loading="isSubmitting" :disabled="settings.deleted">
					{{ $t('settings.vrsSettingsForm.buttonLabel') }}
				</Button>
				<Tooltip :title="$t('settings.vrsSettingsForm.activeSignalsMessages.applyRecommendationsTooltip')">
					<Button
						@click="onApplyRecommendations"
						:disabled="settings.deleted || !canApplyRecommendations"
					>
						{{ $t('settings.vrsSettingsForm.activeSignalsMessages.applyRecommendationsButton') }}
					</Button>
				</Tooltip>
				<Tooltip v-if="settings.status === 'stopped'" :title="$t('settings.sub-networks.vrsSettingsForm.restartDisabledTooltip')">
					<span>
						<PopconfirmButton
							:title="$t('settings.vrsSettingsForm.activeSignalsMessages.restartConfirmation.title')"
							:ok-text="$t('settings.vrsSettingsForm.activeSignalsMessages.restartConfirmation.confirm')"
							:cancel-text="$t('settings.vrsSettingsForm.activeSignalsMessages.restartConfirmation.cancel')"
							:loading="isSubmitting"
							:disabled="settings.deleted || settings.status === 'stopped'"
							:onConfirm="onRestartConfirm"
						>
							{{ $t('settings.vrsSettingsForm.activeSignalsMessages.restartButtonLabel') }}
						</PopconfirmButton>
					</span>
				</Tooltip>
				<PopconfirmButton
					v-else
					:title="$t('settings.vrsSettingsForm.activeSignalsMessages.restartConfirmation.title')"
					:ok-text="$t('settings.vrsSettingsForm.activeSignalsMessages.restartConfirmation.confirm')"
					:cancel-text="$t('settings.vrsSettingsForm.activeSignalsMessages.restartConfirmation.cancel')"
					:loading="isSubmitting"
					:disabled="settings.deleted || settings.status === 'stopped'"
					:onConfirm="onRestartConfirm"
				>
					{{ $t('settings.vrsSettingsForm.activeSignalsMessages.restartButtonLabel') }}
				</PopconfirmButton>
			</Space>
		</VerticalLayout>
	</Skeleton>
</template>

<script>
import { toRef, ref, watch, nextTick, computed } from 'vue';
import { TypographyText, Skeleton, Space, Button, Tooltip, message } from 'ant-design-vue';
import { QuestionCircleOutlined } from '@ant-design/icons-vue';

import { useI18n } from 'vue-i18n'

import Api from '@/api/Api';
import Utils from '@/api/Utils';
import Validators from '@/api/Validators';

import VerticalLayout from '@/components/common/VerticalLayout.vue';
import DividerAutoDirection from '@/components/common/DividerAutoDirection.vue';
import PopconfirmButton from '@/components/common/PopconfirmButton.vue';
import SettingsInfoBanner from '@/components/common/SettingsInfoBanner.vue';

import BandActiveSignals from '@/components/settings/subNetworks/activeSignalsForm/BandActiveSignals.vue';
import {
	applySignalRecommendations,
	canApplySignalRecommendations,
} from '@/components/settings/subNetworks/activeSignalsForm/activeSignalsRecommendationsUtils';

import { useProvideInjectFormChangedStatuses } from '@/utils/useProvideInject';
import { showErrorMessage } from '@/components/common/errorMessage';

const ACTIVE_SIGNALS_FORM_NAME = 'ActiveSignalsForm';


const checkValidData = (data, t) => {
	for (const gnssItem of data) {
		if (![0, 2].includes(gnssItem.bands.filter(band => band.checked).length)) {
			message.error(t('settings.vrsSettingsForm.activeSignalsMessages.bandsCountError', {
				gnssCode: t('common.gnssLabels.' + gnssItem.gnssCode)
			}));
			return false;
		}
		for (const band of gnssItem.bands) {
			if (band.checked && band.signals.length === 0) {
				message.error(t('settings.vrsSettingsForm.activeSignalsMessages.signalsCountError', {
					gnssCode: t('common.gnssLabels.' + gnssItem.gnssCode),
					band: band.key,
				}));
				return false;
			}
		}
	}
	return true;
}


export default {
	components: {
		TypographyText, Skeleton, Space, Button, Tooltip,
		QuestionCircleOutlined,
		VerticalLayout, DividerAutoDirection, PopconfirmButton, SettingsInfoBanner, BandActiveSignals,
	},
	props: ['loading', 'status', 'settings', 'onUpdateField'],
	setup(props) {
		const { t } = useI18n();
		const signalsSettings = ref([]);
		const vrsActiveSignalsRef = ref([]);
		const vrsSelectedSignalsRef = ref([]);
		const vrsSignalsStats = ref([]);

		const { injectValue } = useProvideInjectFormChangedStatuses;
		const formChangedStatuses = injectValue();
		const { enableWatch, pauseWatch, resetChangedState } = Validators.useWatchFormChanges(
			signalsSettings,
			formChangedStatuses,
			ACTIVE_SIGNALS_FORM_NAME,
		);
		const formsChanged = computed(() => !!formChangedStatuses[ACTIVE_SIGNALS_FORM_NAME]);

		const canApplyRecommendations = computed(() => {
			if (settingsRef.value.deleted || vrsSignalsStats.value.length === 0) {
				return false;
			}
			return canApplySignalRecommendations(signalsSettings.value, vrsSignalsStats.value);
		});

		const updateSignalsSettings = () => {
			pauseWatch();
			signalsSettings.value = vrsActiveSignalsRef.value.map(item => {
				const selectedGnss = Utils.findItem(vrsSelectedSignalsRef.value, item.gnssCode, 'gnssCode', null);
				const selectedBands = (selectedGnss != null) ? selectedGnss.bands.filter(band => band.checked).map(band => band.key) : [];
				return {
					gnssCode: item.gnssCode,
					bands: item.bands.map(band => {
						let selectedSignals = [];
						if (selectedGnss != null) {
							const selectedBandData = Utils.findItem(selectedGnss.bands, band.key, 'key', null);
							if (selectedBandData != null) {
								selectedSignals = selectedBandData.signals;
							}
						}
						return {
							key: band.key,
							signals: band.signals.map(signal => ({
								value: signal,
								label: signal,
							})),
							checked: selectedBands.includes(band.key),
							selectedSignals,
						}
					}),
				};
			});
			nextTick(enableWatch);
		}

		const statusData = toRef(props, 'status');
		watch(statusData, value => {
			vrsActiveSignalsRef.value = value.vrsActiveSignals;
			updateSignalsSettings();
		});

		const settingsRef = toRef(props, 'settings');

		const fetchVrsSignalsStats = () => {
			if (settingsRef.value.id && !settingsRef.value.deleted) {
				Api.subNetworkVrsSignalsStatsGet(settingsRef.value.id, response => {
					if (response.status === 'ok') {
						vrsSignalsStats.value = response.vrsSignalsStats;
					} else {
						showErrorMessage(response.errors);
					}
				});
			}
		};

		watch(settingsRef, value => {
			vrsSelectedSignalsRef.value = value.vrsActiveSignals || [];
			updateSignalsSettings();
			fetchVrsSignalsStats();
		}, { immediate: true });

		const isSubmitting = ref(false);
		const onSubmit = () => {
			const data = signalsSettings.value.map(item => {
				const bands = item.bands.map(band => ({
					key: band.key,
					checked: band.checked,
					signals: band.selectedSignals,
				}));
				return {
					gnssCode: item.gnssCode,
					bands,
				}
			});
			if (checkValidData(data, t)) {
				isSubmitting.value = true;
				props.onUpdateField('vrs_active_signals', data, response => {
					isSubmitting.value = false;
					if (response.status === 'ok') {
						resetChangedState();
						message.success(t('settings.vrsSettingsForm.messages.success'));
					}
				});
			}
		}
		const onRestartConfirm = () => {
			isSubmitting.value = true;
			props.onUpdateField('status', 'restarting', response => {
				isSubmitting.value = false;
				if (response.status === 'ok') {
					message.success(t('settings.vrsSettingsForm.activeSignalsMessages.restartConfirmation.success'));
				}
			});
		}

		const updateBand = (gnssCode, bandIndex, updatedBand) => {
			const gnssItem = signalsSettings.value.find(item => item.gnssCode === gnssCode);
			if (gnssItem && gnssItem.bands[bandIndex]) {
				gnssItem.bands[bandIndex] = updatedBand;
			}
		}

		const onApplyRecommendations = () => {
			const { settings, changed } = applySignalRecommendations(
				signalsSettings.value,
				vrsSignalsStats.value,
			);
			if (changed) {
				signalsSettings.value = settings;
			}
		}

		return {
			signalsSettings,
			vrsSignalsStats,
			isSubmitting,
			onSubmit,
			onRestartConfirm,
			onApplyRecommendations,
			canApplyRecommendations,
			updateBand,
			formsChanged,
		};
	},
}
</script>

<style scoped>
.signals-settings {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(270px, 1fr));
	gap: 8px 24px;
}
</style>
